/**
 * The composer's own controls: model, posture, and — once a run exists —
 * what that run has spent.
 *
 * Lives beside the attachment button, at the left end of the composer's
 * action row. thread.aui.tsx (vendored, re-applied from the registry on every
 * re-install) hosts it with a one-line patch; everything it needs stays in
 * this file so that patch never grows.
 *
 * The model/posture pickers are live whether or not a run is running:
 * they set the global launch state (`launchModel`/`launchAgent`/
 * `launchEffort`/`launchMode`), which is what the NEXT start reads. With a
 * run in front, the model picker DISPLAYS the model that run runs.
 * Once a run exists, a small ring says how full its context is (numbers
 * and cost in its tooltip).
 * runmeters.tsx keeps the full breakdown behind its button; this is the
 * glance version.
 *
 * Renders nothing when it has nothing honest to say: no usable agents and no
 * run running.
 */
import { useContext, useMemo } from "react";
import { FolderIcon, SlidersHorizontalIcon } from "lucide-react";
import { bridge } from "../lib/bridge";
import { ChatSurface } from "../lib/surface";
import { ContextCardButton, PastPromptsButton } from "./composercards";
import { PromptLibraryPanel } from "./promptlib";
import { QuotaChip } from "./quota";
import { RunMeterCard, type RunUsage } from "./runmeters";
import { ModelChoice } from "./model-choice";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { mono } from "./assistant-ui/elements/surfaces";
import { cn } from "@/lib/utils";
import { MODES, MODE_LABEL } from "../lib/constants";
import { contextShare, windowFor } from "../lib/meter.mjs";
import { selectActiveConsole, useBoard } from "../lib/board";
import { CHAT_AGENTS, useChat } from "../lib/chat";
import { runningModelName } from "../lib/models.mjs";
import { money, tokens } from "../lib/fmt";
import type { LaunchMode } from "../lib/types";
import type { UsableAgent } from "../lib/types";

/** How full the context is, as a ring; the numbers are its tooltip. */
function ContextRing({ share, label }: { share: number; label: string }) {
  const c = 2 * Math.PI * 6;
  return (
    <span role="img" aria-label={label} title={label} className="shrink-0">
      <svg viewBox="0 0 16 16" className="size-3.5 -rotate-90" aria-hidden="true">
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <circle
          cx="8"
          cy="8"
          r="6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - share)}
        />
      </svg>
    </span>
  );
}

/** ModelChoice's trigger is sized for the launcher panel (h-9, w-full) — too
 *  tall for a composer row of size-7 icon buttons. Its own file is off
 *  limits here, so this reclaims the trigger from outside by its data-slot,
 *  the same idiom brand.tsx's AgentLogo uses to reclaim ClaudeLogo's
 *  hardcoded fill without touching the vendored component: a descendant CSS
 *  rule outranks the trigger's own utility classes on specificity alone. */
const compactModelChoice = cn(
  "[&_[data-slot=model-selector-trigger]]:h-7",
  "[&_[data-slot=model-selector-trigger]]:w-auto",
  // ⚠️ WIDE ENOUGH FOR THE DEFAULT LABEL. At max-w-36 the trigger rendered
  // "whatever t…" — the model control, truncated mid-word, saying nothing.
  // The long opencode ids still truncate, and that is fine: they are long on
  // purpose and the picker spells them out.
  "[&_[data-slot=model-selector-trigger]]:max-w-56",
  "[&_[data-slot=model-selector-trigger]]:gap-1",
  "[&_[data-slot=model-selector-trigger]]:rounded-full",
  "[&_[data-slot=model-selector-trigger]]:border-transparent",
  "[&_[data-slot=model-selector-trigger]]:bg-foreground/[0.04]",
  "[&_[data-slot=model-selector-trigger]]:px-2",
  "[&_[data-slot=model-selector-trigger]]:py-1",
  "[&_[data-slot=model-selector-trigger]]:text-xs",
);

interface ComposerSource {
  usage: RunUsage | null;
  model: string;
  runningModel: { id: string; name: string; title?: string } | undefined;
  agents: UsableAgent[];
  launchMode: LaunchMode;
  setLaunchMode: (m: LaunchMode) => void;
  setConsoleMode: ((key: number, mode: string) => void) | null;
  activeKey: number | null;
}

/** Hook that returns the data the composer needs, whether on Code or Chat surface. */
function useComposerSource(): ComposerSource {
  const isChat = useContext(ChatSurface);
  const launchMode = useBoard((s) => s.launchMode);
  const setLaunchMode = useBoard((s) => s.setLaunchMode);
  const localAgents = useBoard((s) => s.localAgents);
  /* Stable identity: ModelChoice rebuilds its whole model list (a storage read per
     model) whenever `agents` changes, and this hook re-runs on every console publish. */
  const usable = useMemo(
    () => localAgents.filter((a) => a.ok && (!isChat || (CHAT_AGENTS as readonly string[]).includes(a.name))),
    [localAgents, isChat],
  );

  if (isChat) {
    const activeId = useChat((s) => s.activeId);
    const threads = useChat((s) => s.threads);
    const thread = activeId ? threads[activeId] : null;
    const usage = thread?.usage ?? null;
    const model = usage?.model ? runningModelName(usage.model, usage.model) : "";
    // Chat's model is chosen per turn, so the trigger shows the run's only
    // while one is in flight; after that the pick is what the next turn uses.
    const runningModel = !thread?.busy
      ? undefined
      : thread.agent === "zevet"
        ? { id: "zevet:auto", name: thread.route ? `Zevet · ${thread.route}` : "Zevet", title: thread.routeWhy }
        : usage?.model
          ? { id: `${thread.agent}:${usage.model}`, name: model || "Default" }
          : undefined;
    // The rows Code offers, less the agents Chat has no adapter for (gemini,
    // meta): a row that would run as another agent is not a choice.
    return {
      usage,
      model,
      runningModel,
      agents: usable,
      launchMode,
      setLaunchMode,
      setConsoleMode: null,
      activeKey: null,
    };
  }

  // Code surface
  const active = useBoard(selectActiveConsole);
  useBoard((s) => s.myConsoles);
  const setConsoleMode = useBoard((s) => s.setConsoleMode);
  const model = active ? runningModelName(active.usage.model, active.model) : "";
  const runningModel = active
    ? active.nextModel
      ? { id: `${active.agent}:${active.nextModel}`, name: runningModelName("", active.nextModel) }
      : active.agent === "zevet"
      ? { id: "zevet:auto", name: active.route ? `Zevet · ${active.route}` : "Zevet", title: active.routeWhy }
      : { id: `${active.agent}:${active.usage.model || active.model}`, name: model || "Default" }
    : undefined;
  return {
    usage: active?.usage ?? null,
    model,
    runningModel,
    agents: usable,
    launchMode,
    setLaunchMode,
    setConsoleMode,
    activeKey: active?.key ?? null,
  };
}

const NO_FOLDER = "::none";
const PICK_FOLDER = "::pick";
const base = (dir: string) => dir.split(/[\\/]+/).filter(Boolean).pop() || dir;

/** Chat + Work: the folder this thread works in. None is plain chat; a folder
 *  gives the agent its tools there. */
function FolderChip() {
  const dir = useChat((s) => s.chats.find((c) => c.id === s.activeId)?.folder ?? (s.activeId ? "" : s.draftFolder));
  const setFolder = useChat((s) => s.setFolder);
  const workspaces = useBoard((s) => s.localWorkspaces);
  const refresh = useBoard((s) => s.refreshLocalWorkspaces);
  const dirs = workspaces.map((w) => w.dir);
  if (dir && !dirs.includes(dir)) dirs.unshift(dir);
  return (
    <Select
      value={dir || NO_FOLDER}
      onValueChange={(v: string | null) => {
        if (!v) return;
        if (v === NO_FOLDER) void setFolder("");
        else if (v === PICK_FOLDER)
          void bridge.local?.addWorkspace().then(async (w) => {
            if (!w) return;
            await refresh();
            await setFolder(w.dir);
          });
        else void setFolder(v);
      }}
    >
      <SelectTrigger
        size="sm"
        data-slot="folder-chip"
        className="h-7 max-w-40 shrink-0 gap-1 rounded-full border-transparent bg-foreground/[0.04] px-2 text-xs"
        aria-label="Folder"
        title={dir || "Folder"}
      >
        <FolderIcon className="size-3.5" aria-hidden="true" />
        <SelectValue>{(v: string) => (v === NO_FOLDER ? "Folder" : base(v))}</SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        <SelectItem value={NO_FOLDER}>None</SelectItem>
        {dirs.map((d) => (
          <SelectItem key={d} value={d}>
            {base(d)}
          </SelectItem>
        ))}
        <SelectItem value={PICK_FOLDER}>Choose folder…</SelectItem>
      </SelectContent>
    </Select>
  );
}

/** claude launch flags with no other home: `--continue` (the latest session in
 *  this folder, for the next start only) and `--add-dir` (more folders the
 *  agent may touch). Effort lives in the model picker. Code surface only. */
function LaunchExtras() {
  const cont = useBoard((s) => s.launchContinue);
  const setCont = useBoard((s) => s.setLaunchContinue);
  const dirs = useBoard((s) => s.launchAddDirs);
  const setDirs = useBoard((s) => s.setLaunchAddDirs);
  const on = cont || dirs.trim().length > 0;
  return (
    <Popover>
      <PopoverTrigger
        aria-label="Session options"
        title="Continue the latest session here · extra folders"
        className={cn(
          "flex h-7 shrink-0 items-center rounded-full px-2 text-xs",
          on ? "bg-foreground/10 text-foreground" : "bg-foreground/[0.04] text-foreground/60",
        )}
      >
        <SlidersHorizontalIcon className="size-3.5" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-72 flex-col gap-3 text-xs">
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={cont} onChange={(e) => setCont(e.target.checked)} className="mt-0.5" />
          <span>Continue the latest Claude session in this folder (next start only)</span>
        </label>
        <label className="flex flex-col gap-1">
          <span>Extra folders Claude may use, one path per line</span>
          <Textarea value={dirs} onChange={(e) => setDirs(e.target.value)} rows={3} className="text-xs" />
        </label>
      </PopoverContent>
    </Popover>
  );
}

export function ComposerControls() {
  const { usage, model, runningModel, agents, launchMode, setLaunchMode, setConsoleMode, activeKey } = useComposerSource();
  const isChat = useContext(ChatSurface);

  /* ⚠️ ONE MODEL LABEL, AND IT IS THIS RUN'S. A second, mono
     "GPT-5.6-Terra 354k/200k 78% cached" beside the picker read as two models
     and as jargon — but folding the running model into the launch picker then
     showed "Opus 5.5", the NEXT start's default, over a Sonnet 5 run. With a
     run in front, the picker stays live but DISPLAYS what that run runs
     (usage.model, else what it was started with); a pick still sets the
     next start. Token counts and cost live in the ring's tooltip. */
  const facts = usage
    ? (() => {
        const window = windowFor(usage.window, usage.model);
        const share = contextShare(usage.context, usage.window, usage.model);
        const detail = [
          model,
          usage.context != null ? `${tokens(usage.context)} of ${tokens(window)} context` : null,
          usage.cacheHit != null ? `${Math.round(usage.cacheHit)}% cached` : null,
          usage.cost != null ? money(usage.cost) : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <span className={cn(mono, "flex min-w-0 shrink items-center gap-1.5 text-foreground/50")}>
            {usage.context != null && <ContextRing share={share} label={detail} />}
            {/* The provider's own rate-limit window — only once it is nearly
                used up. */}
            <QuotaChip />
          </span>
        );
      })()
    : null;

  // No usable agent to start or fork with — the pickers have nothing to
  // offer. Facts about an already-running run still stand on their own;
  // with neither, there is nothing honest to say.
  if (!agents.length) return facts;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {/* Left of the chat box: the prompts you have written before. A button,
          not a collapsed row — see composercards.tsx for why the card is an
          overlay and not a dropdown.

          ⚠️ ALWAYS MOUNTED. It was gated on `active`, and that gate was the
          last thing moving the chat box on its own: with no run the row
          held the model picker and Send, and the moment a run started this
          button and ComposerExtras appeared on either side of them, growing
          the row and pushing the box 16px down. Measured in the running app
          2026-09-21 — composer top 669 empty, 685 with the buttons.

          Nothing needed relaxing to fix it. PromptLibraryPanel was already
          written for a null run: it disables Save and says "No active
          run to save from". The gate was preventing a state the panel
          already handled. */}
      <PastPromptsButton>
        <PromptLibraryPanel />
      </PastPromptsButton>
      <div className={compactModelChoice}>
        <ModelChoice agents={agents} runningId={runningModel?.id} runningName={runningModel?.name} runningTitle={runningModel?.title} />
      </div>

      {/* The run in front takes the pick on its next turn
          (`setConsoleMode` parks it while a turn runs); the next start takes it
          too. This is the only posture control — the rail's rows lost theirs. */}
      <Select
        value={activeKey && !isChat ? (useBoard.getState().myConsoles.find((c) => c.key === activeKey)?.nextMode ?? useBoard.getState().myConsoles.find((c) => c.key === activeKey)?.mode ?? launchMode) : launchMode}
        onValueChange={(v: string | null) => {
          if (!v) return;
          setLaunchMode(v as LaunchMode);
          if (!isChat && activeKey && setConsoleMode) setConsoleMode(activeKey, v);
        }}
      >
        <SelectTrigger
          size="sm"
          className="h-7 shrink-0 gap-1 rounded-full border-transparent bg-foreground/[0.04] px-2 text-xs"
          aria-label="Permissions"
        >
          <SelectValue>{(v: string) => MODE_LABEL[v] ?? v}</SelectValue>
        </SelectTrigger>
        <SelectContent align="start">
          {MODES.map((m) => (
            <SelectItem key={m.id} value={m.id}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent      >
      </Select>

      {isChat ? <FolderChip /> : <LaunchExtras />}

      {facts}
    </div>
  );
}

/**
 * The right end of the composer's action row: context and spend, behind a
 * button, as a card.
 *
 * Separate from ComposerControls because the row has two groups and this one
 * belongs in the other — see the COPY patch in board/scripts/sync-registry.mjs
 * that hosts both.
 *
 * ⚠️ THE BUTTON IS ALWAYS HERE; only its CONTENTS wait for usage. It used to
 * return null until a run reported numbers, which meant the composer's action
 * row grew by a button the moment an agent started and the chat box slid down
 * with it — "the chatbox ... should never change positions or resize
 * autonomously", broken by the very row built to obey it.
 *
 * And the gate read `strip.live`, which is ONE set of numbers for however many
 * runs are running and which board.ts only ever patches — never clears. So
 * once any agent had run, a brand-new run inherited the button and the card
 * under it showed the other agent's context. RunMeterCard reads the run's
 * own usage now and says so there.
 */
export function ComposerExtras() {
  const { usage } = useComposerSource();
  return (
    <ContextCardButton>
      {usage && usage.context != null ? (
        <RunMeterCard usage={usage} />
      ) : (
        <p className="text-foreground/50 text-xs">Nothing reported yet — this fills in once the agent speaks.</p>
      )}
    </ContextCardButton>
  );
}